import * as React from 'react';
import * as PropTypes from 'prop-types'
import { clickHandler } from "../../../utils/util";
import { IBooking } from "./types";
import * as ct from "../../global/controls";
import * as v from "../../global/validation";
import { ValidationError } from "../../../store/global/types";
import { ApplicationState } from "../../../store";
import { connect } from "react-redux";
import * as api from "../../../store/apiClient";
import Loading from "../../global/loading";
import ApiError from "../../global/apiError";

interface MappedReduxState {
    validationErrors: ValidationError[];
}

interface Actions {
    close: (stockDistributed: boolean) => void;
    logout: () => void;
}

interface LocalProps {
    booking: IBooking;
}

type BookingStockItemsPanelProps = MappedReduxState & Actions & LocalProps;

interface BookingStockLine {
    stockMovementId: string;
    productName: string;
    productVariantName: string;
    quantityToDistribute: ct.FormValue<number>;
    totalQuantity: number;
    distributedQuantity: number;
    remainingQuantity: number;
}

interface BookingStockItemsPanelState {
    bookingStockLines: BookingStockLine[];
    validationError: string | null;
    isSaving: boolean;
    savingError: api.ApiError | null;
}

interface IContext {
    t: (val: string) => string;
}

class BookingStockItemsPanel extends React.Component<BookingStockItemsPanelProps, BookingStockItemsPanelState> {

    constructor(props: BookingStockItemsPanelProps) {
        super(props);
        this.state = this.buildStateFromProps(props);
    }

    static contextTypes = {
        t: PropTypes.func
    }

    private buildStateFromProps(props: BookingStockItemsPanelProps): BookingStockItemsPanelState {
        return {
            bookingStockLines: this.buildBookingStockLines(),
            validationError: null,
            isSaving: false,
            savingError: null,
        };
    }

    private buildBookingStockLines(): BookingStockLine[] {
        const { booking } = this.props;
        return (booking?.stockInventoryItems ?? []).map(i => {
            const total = Math.abs(i.expectedFulfillmentQuantity);
            const distributed = Math.abs(i.confirmedFulfillmentQuantity ?? 0);
            const remaining = total - distributed;
            return {
                stockMovementId: i.stockMovementId,
                productName: i.productName,
                productVariantName: i.productVariantName,
                quantityToDistribute: this.validateQuantityToDistribute(i.stockMovementId, remaining, remaining),
                totalQuantity: total,
                distributedQuantity: distributed,
                remainingQuantity: remaining,
            };
        }).filter(i => i.remainingQuantity > 0);
    }

    quantityToDistributeDoesNotExceedRemaining = (val: number, remaining: number) => {
        return val > remaining ? 'BookingStockItemsPanel:quantityExceedsMaxRemaining' : undefined;
    };

    validateQuantityToDistribute = (lineId: string, val: number, remaining: number) => v.validate(val, `${lineId}_quantityToDistribute`, [v => this.quantityToDistributeDoesNotExceedRemaining(val, remaining)], []);

    handleQuantityToDistributeChanged = (id: string, val: number | string, remainingQuantity: number) => {
        const newQuantity = typeof (val) === 'string' ? parseInt(val) : val;
        const constrainedVal = isNaN(newQuantity) || newQuantity < 0 ? 0 : newQuantity > remainingQuantity ? remainingQuantity : newQuantity;
        this.setState(prevState => ({ bookingStockLines: prevState.bookingStockLines.map(i => i.stockMovementId === id ? { ...i, quantityToDistribute: this.validateQuantityToDistribute(id, constrainedVal, i.remainingQuantity) } : i)}));
    };

    decrementQuantityToDistribute = (id: string, remainingQuantity: number) =>
        this.setState(prevState => ({
            bookingStockLines: prevState.bookingStockLines.map(i => {
                if (i.stockMovementId !== id) return i;
                const newValue = i.quantityToDistribute.value - 1;
                if (newValue < 0) return i;
                return {
                    ...i,
                    quantityToDistribute: this.validateQuantityToDistribute(id, newValue, i.remainingQuantity)
                };
            })
        }));

    incrementQuantityToDistribute = (id: string, remainingQuantity: number) =>
        this.setState(prevState => ({
            bookingStockLines: prevState.bookingStockLines.map(i => {
                if (i.stockMovementId !== id) return i;
                const newValue = i.quantityToDistribute.value + 1;
                if (newValue > i.remainingQuantity) return i;
                return {
                    ...i,
                    quantityToDistribute: this.validateQuantityToDistribute(id, newValue, i.remainingQuantity)
                };
            })
        }));

    private onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
    }

    private distributeStock = () => {
        const { logout, close } = this.props;
        const { bookingStockLines } = this.state;

        const stockItemsToDistribute = bookingStockLines.map(i => ({
            productStockMovementId: i.stockMovementId,
            quantityToDistribute: i.quantityToDistribute.value
        }));
        const body = {
            stockItemsToDistribute
        };

        api.postWithAuth(`api/v1/productStock/distributeStock`, body, logout)
            .subscribe(response => {
                this.setState({ isSaving: false, savingError: null }, () => close(true))
            },
            (err: api.ApiError) => { this.setState({ isSaving: false, savingError: err }) });
    }

    private save = () => {
        if (!v.isValid(this.state)) {
            this.setState({ validationError: this.context.t('Global:formNotValid') });
            // TODO: Show error message!
        } else {
            this.setState({ validationError: null, isSaving: true }, this.distributeStock);
        }
    }

    render() {
        const { t } = this.context;
        const { close } = this.props;
        const { bookingStockLines, isSaving, savingError } = this.state;

        if (isSaving) return <Loading />;
        if (savingError) return <ApiError error={ savingError } />;

        return (
            <div>
                <h3>{t('BookingStockItemsPanel:title')}</h3>
                <form className='data-form' onSubmit={this.onFormSubmit} autoComplete='off'>
                    <table className='table table-striped table-sm table-condensed stock-bookingstockptemspanel-data-table'>
                        <thead>
                            <tr>
                                <th>{t('BookingStockItemsPanel:tableHeaderProduct')}</th>
                                <th>{t('BookingStockItemsPanel:tableHeaderQuantityToDistribute')}</th>
                                <th>{t('BookingStockItemsPanel:tableHeaderQuantityOrdered')}</th>
                                <th>{t('BookingStockItemsPanel:tableHeaderQuantityDistributed')}</th>
                                <th>{t('BookingStockItemsPanel:tableHeaderQuantityRemaining')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookingStockLines.map(i => {
                                return <tr key={i.stockMovementId}>
                                    <td>
                                        {i.productName} {i.productVariantName && <>({i.productVariantName})</>}
                                    </td>
                                    <td>
                                        <div className='flex flex-start'>
                                            <button onClick={e => clickHandler(e, () => this.decrementQuantityToDistribute(i.stockMovementId, i.remainingQuantity))} className='btn btn-primary'>-</button>
                                            <ct.NumericText id={`${i.stockMovementId}_quantityToDistribute`} labelKey='' value={i.quantityToDistribute} callback={(val) => this.handleQuantityToDistributeChanged(i.stockMovementId, val, i.remainingQuantity)} dp={0} />
                                            <button onClick={e => clickHandler(e, () => this.incrementQuantityToDistribute(i.stockMovementId, i.remainingQuantity))} className='btn btn-primary'>+</button>
                                        </div>
                                    </td>
                                    <td>
                                        {i.totalQuantity}
                                    </td>
                                    <td>
                                        {i.distributedQuantity}
                                    </td>
                                    <td>
                                        {i.remainingQuantity}
                                    </td>
                                </tr>
                            })}
                        </tbody>
                    </table>
                    <div className='btn-toolbar' style={({ marginTop: '20px' })}>
                        <button className='btn btn-primary' disabled={isSaving} onClick={e => clickHandler(e, this.save)}>{t('Global:save')}</button>
                        <button className='btn btn-basic' disabled={isSaving} onClick={e => clickHandler(e, () => close(false))}>{t('Global:cancel')}</button>
                    </div>
                </form>
            </div>
        );
    }
}

const mapStateToProps = (state: ApplicationState) => ({
    validationErrors: state.products.validationErrors,
});

export default connect(
    mapStateToProps,
)(BookingStockItemsPanel);
